import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { StageWheel, WHEEL_LETTERS } from './StageWheel';
import { StageReel } from './StageReels';
import {
  DESIGN_W,
  DESIGN_H,
  GOLD,
  STAGE_LAYOUTS,
  OVERLAYS,
  SHOW_SHORT,
} from './stageConfig';
import { SHOWS, getShowState, getNextDraw, fmtHMS, resultDisplayUntil } from '../lib/shows';
import { useIST } from '../hooks/useIST';
import { Menu } from 'lucide-react';
import { ResultsChart } from '../components/ResultsChart';

function useStageFit() {
  const compute = () => {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    return { scale: Math.min(vw / DESIGN_W, vh / DESIGN_H) };
  };
  const [fit, setFit] = useState(compute);
  useEffect(() => {
    const onResize = () => setFit(compute());
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);
    window.visualViewport?.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('orientationchange', onResize);
      window.visualViewport?.removeEventListener('resize', onResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return fit;
}

const goldText = {
  color: GOLD,
  textShadow: '0 0 18px rgba(245,197,66,0.45), 0 2px 2px rgba(0,0,0,0.6)',
};

const hashSeed = (str) => {
  let h = 2166136261;
  for (let i = 0; i < str.length; i++) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const mulberry32 = (a) => () => {
  a |= 0;
  a = (a + 0x6d2b79f5) | 0;
  let t = Math.imul(a ^ (a >>> 15), 1 | a);
  t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

const buildSpinPlan = (seedStr) => {
  const rng = seedStr ? mulberry32(hashSeed(seedStr)) : Math.random;
  const order = [0, 1, 2, 3, 4];
  for (let i = order.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [order[i], order[j]] = [order[j], order[i]];
  }
  const times = [];
  let t = 12 + rng() * 1;
  for (let rank = 0; rank < 5; rank++) {
    if (rank > 0) t += 1 + rng() * 0.75;
    times.push(t);
  }
  return {
    durations: order.map((rank) => times[rank]),
    lastIndex: order.indexOf(4),
    total: Math.max(...times),
  };
};

export default function LiveStagePage() {
  const ist = useIST();
  const { scale } = useStageFit();
  const params = useMemo(() => new URLSearchParams(window.location.search), []);
  const showParam = params.get('show');
  const spinParam = params.get('spin');

  const auto = useMemo(() => getShowState(ist), [ist]);
  const nextDraw = getNextDraw(ist);
  const [activeShowId, setActiveShowId] = useState(null);
  const show =
    (activeShowId && SHOWS.find((s) => s.id === activeShowId)) ||
    SHOWS.find((s) => s.id === showParam) ||
    auto.show;
  const layout = STAGE_LAYOUTS[show.theme];
  const shortLabel = SHOW_SHORT[show.id];
  const day3 = ist.weekday.slice(0, 3).toUpperCase();

  const [spinKey, setSpinKey] = useState(0);
  const [chartOpen, setChartOpen] = useState(false);
  const [letterTarget, setLetterTarget] = useState(null);
  const [digitTargets, setDigitTargets] = useState(null);
  const [reelPlan, setReelPlan] = useState({ durations: [13, 14.5, 16, 17.5, 19], lastIndex: 4, total: 19 });
  const [startAt, setStartAt] = useState(0);
  const [result, setResult] = useState(null);
  const [drawing, setDrawing] = useState(false);
  const drawingRef = useRef(false);
  const playedRef = useRef(new Set());
  const initialisedRef = useRef(false);
  const stageSeenRef = useRef(null);
  const resultMetaRef = useRef(null);
  const serverOffsetRef = useRef(0);

  const runDraw = (letterIdx = null, digits = null, anchorISO = null) => {
    if (drawingRef.current) return;
    const plan = buildSpinPlan(anchorISO);
    const elapsed = anchorISO
      ? Math.max(0, (Date.now() + serverOffsetRef.current - new Date(anchorISO).getTime()) / 1000)
      : 0;
    const li = letterIdx ?? Math.floor(Math.random() * WHEEL_LETTERS.length);
    const dg = digits ?? Array.from({ length: 5 }, () => Math.floor(Math.random() * 10));
    if (elapsed >= plan.total) {
      setLetterTarget(li);
      setDigitTargets(dg);
      setResult({ letter: WHEEL_LETTERS[li], digits: dg });
      return;
    }
    drawingRef.current = true;
    setReelPlan({ durations: plan.durations, lastIndex: plan.lastIndex, total: plan.total });
    setStartAt(elapsed);
    setLetterTarget(li);
    setDigitTargets(dg);
    setResult(null);
    setDrawing(true);
    setSpinKey((k) => k + 1);
  };

  useEffect(() => {
    if (!spinParam) return undefined;
    const t = setTimeout(() => {
      let letterIdx = null;
      let digits = null;
      if (spinParam !== '1') {
        const [letter, num] = spinParam.split(':');
        const idx = WHEEL_LETTERS.indexOf((letter || '').toUpperCase());
        if (idx >= 0) letterIdx = idx;
        if (num && /^\d{1,5}$/.test(num)) {
          digits = num.padStart(5, '0').split('').map(Number);
        }
      }
      runDraw(letterIdx, digits);
    }, 1200);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinParam]);

  const handleLastReelSettled = useCallback(() => {
    if (letterTarget == null || !digitTargets) return;
    drawingRef.current = false;
    setDrawing(false);
    setResult({ letter: WHEEL_LETTERS[letterTarget], digits: digitTargets });
  }, [letterTarget, digitTargets]);

  useEffect(() => {
    Object.values(STAGE_LAYOUTS).forEach((l) => {
      const img = new Image();
      img.src = l.image;
    });
  }, []);

  useEffect(() => {
    if (spinParam) return undefined;
    const RESULT_TTL = 30 * 60 * 1000;
    const poll = async () => {
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_BACKEND_URL}/api/stage/state`
        );
        if (data.server_now) {
          serverOffsetRef.current = new Date(data.server_now).getTime() - Date.now();
        }
        const freshEnough = (iso) => Date.now() - new Date(iso).getTime() < RESULT_TTL;
        const resultActive = (show, iso) => Date.now() < resultDisplayUntil(show, iso);
        if (data.active_show && data.stage_updated_at !== stageSeenRef.current) {
          const firstSeen = stageSeenRef.current === null && !initialisedRef.current;
          stageSeenRef.current = data.stage_updated_at;
          const resultLocked =
            drawingRef.current ||
            (resultMetaRef.current &&
              resultActive(resultMetaRef.current.show, resultMetaRef.current.updated_at));
          // a published result owns the stage until 10 min before the next show
          if (freshEnough(data.stage_updated_at) && !resultLocked) {
            setActiveShowId(data.active_show);
            if (!firstSeen) {
              resultMetaRef.current = null;
              setResult(null);
              setLetterTarget(null);
              setDigitTargets(null);
            }
          }
        }
        const results = data.results || [];
        if (!initialisedRef.current) {
          initialisedRef.current = true;
          results.forEach((r) => playedRef.current.add(`${r.id}:${r.updated_at}`));
          const fresh = results.filter((r) => resultActive(r.show, r.updated_at));
          if (fresh.length) {
            const latest = fresh.reduce((a, b) => (a.updated_at > b.updated_at ? a : b));
            resultMetaRef.current = { id: latest.id, show: latest.show, updated_at: latest.updated_at };
            setActiveShowId(latest.show);
            const idx = WHEEL_LETTERS.indexOf(latest.letter);
            // joins mid-draw at the exact same synced point as everyone else
            runDraw(idx >= 0 ? idx : 0, latest.digits.split('').map(Number), latest.updated_at);
          }
          return;
        }
        if (drawingRef.current) return;
        // result stays until 10 min before the next show (unless deleted), then time-based stage
        if (resultMetaRef.current) {
          const meta = resultMetaRef.current;
          const stillExists = results.some(
            (r) => r.id === meta.id && r.updated_at === meta.updated_at
          );
          if (!resultActive(meta.show, meta.updated_at) || !stillExists) {
            resultMetaRef.current = null;
            setResult(null);
            setLetterTarget(null);
            setDigitTargets(null);
            setActiveShowId(null);
          }
        }
        const pending = results
          .filter((r) => !playedRef.current.has(`${r.id}:${r.updated_at}`))
          .sort((a, b) => a.updated_at.localeCompare(b.updated_at));
        const r = pending[0];
        if (!r) return;
        playedRef.current.add(`${r.id}:${r.updated_at}`);
        if (!resultActive(r.show, r.updated_at)) return;
        setActiveShowId(r.show);
        resultMetaRef.current = { id: r.id, show: r.show, updated_at: r.updated_at };
        const idx = WHEEL_LETTERS.indexOf(r.letter);
        runDraw(idx >= 0 ? idx : null, r.digits.split('').map(Number), r.updated_at);
      } catch {
        // backend unreachable — retry next poll
      }
    };
    poll();
    const iv = setInterval(poll, 1000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spinParam]);

  const { wheel, boxes } = layout;
  const wheelD = wheel.d * 1.24;
  const { banner, sidePlate, bottomCircle, datePlate, timePlate } = OVERLAYS;

  return (
    <div
      className="fixed inset-0 flex items-center justify-center overflow-hidden bg-black"
      data-testid="live-stage-page"
    >
      <button
        type="button"
        onClick={() => setChartOpen(true)}
        data-testid="live-chart-open"
        className="fixed right-3 top-3 z-40 border border-[#F5C542]/40 bg-black/60 p-2 text-[#F5C542] backdrop-blur-sm transition-colors hover:bg-[#F5C542] hover:text-black"
      >
        <Menu className="h-5 w-5" />
      </button>
      {chartOpen && <ResultsChart onClose={() => setChartOpen(false)} />}
      <div
        className="relative"
        style={{
          width: DESIGN_W,
          height: DESIGN_H,
          transform: `scale(${scale})`,
          transformOrigin: 'center center',
          flexShrink: 0,
        }}
      >
        <img
          key={show.theme}
          src={layout.image}
          alt={`Golden Raja ${show.label} stage`}
          className="absolute inset-0 h-full w-full"
          draggable={false}
          fetchPriority="high"
          decoding="async"
          data-testid="stage-background"
        />

        {layout.liveDot && (
          <div
            className="absolute rounded-full"
            style={{
              left: layout.liveDot.cx - layout.liveDot.r - 3,
              top: layout.liveDot.cy - layout.liveDot.r - 3,
              width: (layout.liveDot.r + 3) * 2,
              height: (layout.liveDot.r + 3) * 2,
              background: '#F7F4EE',
            }}
            data-testid="live-blink-dot"
          >
            <motion.div
              className="absolute rounded-full"
              style={{
                inset: 3,
                background: '#D8232A',
                boxShadow: '0 0 14px rgba(216,35,42,0.95), 0 0 4px rgba(216,35,42,0.9)',
              }}
              animate={{ opacity: [1, 1, 0.05, 0.05] }}
              transition={{ duration: 1.2, repeat: Infinity, times: [0, 0.45, 0.55, 1] }}
            />
          </div>
        )}

        <div
          className="absolute flex items-center justify-center"
          style={{ left: banner.x, top: banner.y, width: banner.w, height: banner.h }}
          data-testid="stage-banner"
        >
          <AnimatePresence mode="wait">
            {result ? (
              <motion.p
                key="result"
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                className="font-display text-[44px] uppercase tracking-[0.15em]"
                style={goldText}
                data-testid="stage-result-text"
              >
                {result.letter} {result.digits.join('')}
              </motion.p>
            ) : (
              <motion.p
                key="idle"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="font-display text-[36px] uppercase tracking-[0.12em]"
                style={goldText}
                data-testid="stage-clock-text"
              >
                {drawing
                  ? `Golden Raja Shine ${ist.weekday} Weekly Draw`
                  : nextDraw.live
                    ? `${SHOW_SHORT[nextDraw.show.id]} Draw · Result Any Moment`
                    : `Next Draw ${SHOW_SHORT[nextDraw.show.id]} · ${fmtHMS(nextDraw.secondsLeft)}`}
              </motion.p>
            )}
          </AnimatePresence>
        </div>

        <div
          className="absolute flex items-center justify-center"
          style={{
            left: sidePlate.x,
            top: sidePlate.y,
            width: sidePlate.w,
            height: sidePlate.h,
          }}
          data-testid="stage-side-plate"
        >
          <span
            className="font-display whitespace-nowrap text-[40px] uppercase tracking-[0.15em]"
            style={{ ...goldText, transform: 'rotate(-90deg)' }}
          >
            {day3} · {shortLabel}
          </span>
        </div>

        <div
          className="absolute flex flex-col items-center justify-center leading-none"
          style={(() => {
            const bc = layout.bottomCircle;
            return bc
              ? { left: bc.cx - bc.d / 2, top: bc.cy - bc.d / 2, width: bc.d, height: bc.d }
              : { left: bottomCircle.x, top: bottomCircle.y, width: bottomCircle.w, height: bottomCircle.h };
          })()}
          data-testid="stage-bottom-circle"
        >
          <span className="font-display" style={{ ...goldText, fontSize: (layout.bottomCircle?.d || 116) * 0.42 }}>
            {shortLabel.split(' ')[0]}
          </span>
          <span className="font-display" style={{ ...goldText, fontSize: (layout.bottomCircle?.d || 116) * 0.24 }}>
            {shortLabel.split(' ')[1]}
          </span>
        </div>

        <div
          className="absolute flex items-center justify-center"
          style={{ left: datePlate.x, top: datePlate.y, width: datePlate.w, height: datePlate.h }}
          data-testid="stage-date-plate"
        >
          <span className="font-display text-[34px] uppercase tracking-[0.08em]" style={goldText}>
            Draw Date : {ist.day}.{ist.month}.{ist.year}
          </span>
        </div>

        {!layout.drawTimeBaked && (
          <div
            className="absolute flex items-center justify-center"
            style={{ left: timePlate.x, top: timePlate.y, width: timePlate.w, height: timePlate.h }}
            data-testid="stage-time-plate"
          >
            <span className="font-display text-[34px] uppercase tracking-[0.08em] text-[#F5F5F5]" style={{ textShadow: '0 2px 3px rgba(0,0,0,0.7)' }}>
              Draw Time / {shortLabel}
            </span>
          </div>
        )}

        <div
          key={`wheel-${show.theme}`}
          className="absolute"
          style={{
            left: wheel.cx - wheelD / 2,
            top: wheel.cy - wheelD / 2,
            width: wheelD,
            height: wheelD,
          }}
        >
          <StageWheel size={wheelD} target={letterTarget} spinKey={spinKey} duration={reelPlan.total} startAt={startAt} />
        </div>

        {boxes.map((box, i) => (
          <StageReel
            key={`${show.theme}-${i}`}
            index={i}
            box={box}
            target={digitTargets ? digitTargets[i] : null}
            spinKey={spinKey}
            duration={reelPlan.durations[i]}
            startAt={startAt}
            onSettled={i === reelPlan.lastIndex ? handleLastReelSettled : undefined}
          />
        ))}
      </div>
    </div>
  );
}
